import { createSlice, PayloadAction, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'store/store';

import * as foldersService from 'services/folders.service';
import * as projectService from 'services/project.service';
import * as accessService from 'services/access.service';
import * as storyService from 'services/story.service';
import * as audioService from 'services/audio.service';
import * as fcService from 'services/finalcut.service';
import { ProjectStatus } from 'services/project.service';
import { GetAvailableFileExtensions, FileType, FontData, GetAvailableFonts } from 'services/file.service';
import { UserInfo, getUserInfo, getStorageSize, UserStorage } from 'services/auth.service';
import { getPaymentPricing, Prices, SubscriptionStatus } from 'services/payment.service';
import { createSafeAsyncThunk } from 'store/store.helper';
import { generateStoryTitleCreditsOverlays } from 'components/app/editor/_mediator/monitor/monitor-req.slice';

import {
  getDefaultFc,
  getDefaultProject,
  getDefaultStory,
  isFcStatusCompleted,
  sortByDate,
} from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { ALL_ACTIVE_FOLDER_ID, UNFILED_FOLDER_ID } from 'components/app/projects/_mediator/projects.params';
import {
  setFoldersAvailable,
  setSelectedFinalcut,
  setSelectedProject,
  setSelectedStory,
} from 'components/app/projects/_mediator/projects.slice';
import { deleteProcessingFc } from 'components/app/finalcuts/_mediator/fc-render-server.slice';
import { fetchFinalCut } from 'components/app/finalcuts/_mediator/fc-data.slice';
import { FileUplId } from './app-files-upload.slice';

const SLICE_ID = 'app__data';

export interface UpdateEntity {
  id: EntityId;
  name: string;
  entity: 'projects' | 'stories' | 'finalcuts' | 'folders';
}

export type EntityId = StoryId | ProjectId | FinalcutId | FileId;
export type FolderType = 'project' | 'file' | 'story' | 'finalcut';

interface MoveToFolder {
  entity: FolderType;
  entityId: EntityId;
  folderId: FolderId | -1;
}

interface StoryWithFinalCut {
  storyId: StoryId;
  withFinalcuts: boolean;
}

interface RemovingRowInstance {
  instance: 'folder' | 'project' | 'file' | 'story' | 'finalcut';
  id: ProjectId | FileId | FileUplId | StoryId | FinalcutId;
  modalOpen: boolean;
}

interface RestoreFcStory {
  finalCutId: FinalcutId;
  storyName: string;
}

export interface Extensions {
  audio: string[];
  video: string[];
  image: string[];
}

interface State {
  user: UserInfo | null;
  storage: UserStorage;
  pricing: Prices;
  extensions: Extensions | null;
  fonts: FontData[] | null;
  folders: foldersService.Folder[] | null;
  projects: projectService.Project[] | null;
  stories: storyService.Story[] | null;
  finalcuts: fcService.FinalCut[];
  removingInstance: RemovingRowInstance | null;
}

const initialState: State = {
  user: null,
  storage: { size: 0, subscriptionId: null, subscriptionType: null, subscriptionStatus: null },
  pricing: {
    productPrices: [],
    subscriptionPrices: [],
  },
  extensions: null,
  fonts: null,
  folders: null,
  projects: null,
  stories: null,
  finalcuts: [],
  removingInstance: null,
};

// T H U N K S

export const fetchUser = createSafeAsyncThunk(SLICE_ID + '/fetchUser', async () => {
  const userData = (await getUserInfo()).data;
  if (userData.subscriptionStatus && userData.subscriptionStatus > SubscriptionStatus.Activated)
    toast.error(`Your subscription status is ${SubscriptionStatus[userData.subscriptionStatus]}`);
  return userData;
});

export const fetchStorageSize = createSafeAsyncThunk(SLICE_ID + '/fetchStorageSize', async () => {
  const response = await getStorageSize();
  return response.data;
});

export const fetchExtensions = createSafeAsyncThunk(SLICE_ID + '/fetchExtensions', async () => {
  const audio = GetAvailableFileExtensions(FileType.audio);
  const video = GetAvailableFileExtensions(FileType.video);
  const image = GetAvailableFileExtensions(FileType.image);
  const [audioRes, videoRes, imageRes] = await Promise.all([audio, video, image]);
  return {
    audio: audioRes.data,
    video: videoRes.data,
    image: imageRes.data,
  };
});

export const fetchFonts = createSafeAsyncThunk('auth/fetchFonts', async () => {
  const response = await GetAvailableFonts();
  return response.data;
});

export const fetchPaymentPricing = createSafeAsyncThunk(SLICE_ID + '/fetchPaymentPricing', async () => {
  const pricing = await getPaymentPricing();
  return pricing?.data ?? [];
});

export const updateAccessType = createSafeAsyncThunk(
  SLICE_ID + '/updateAccessType',
  async (payload: accessService.UpdateAccessType) => {
    const response = await accessService.updateAccessType(payload);
    return response.data;
  }
);

// folders

export const fetchFolders = createSafeAsyncThunk(SLICE_ID + '/fetchFolders', async () => {
  return (await getUserInfo()).data.folders;
});

export const createFolder = createSafeAsyncThunk(SLICE_ID + '/createFolder', async (name: string) => {
  await foldersService.createFolder(name);
});

export const moveToFolder = createSafeAsyncThunk(
  SLICE_ID + '/moveToFolder',
  async (payload: MoveToFolder, { getState, dispatch }) => {
    const { entityId, entity, folderId } = payload;
    const isUnfiled = folderId === UNFILED_FOLDER_ID;
    switch (entity) {
      case 'project':
        if (isUnfiled) await foldersService.unsubscribeProjectFolder(entityId);
        else await foldersService.setProjectFolder(entityId, folderId);
        break;
      case 'file':
        if (isUnfiled) await foldersService.unsubscribeMediaFileFolder(entityId);
        else await foldersService.setMediaFileFolder(entityId, folderId);
        break;
      case 'story':
        if (isUnfiled) await foldersService.unsubscribeStoryFolder(entityId);
        else await foldersService.setStoryFolder(entityId, folderId);
        break;
      case 'finalcut':
        if (isUnfiled) await foldersService.unsubscribeFinalCutFolder(entityId);
        else await foldersService.setFinalCutFolder(entityId, folderId);
        break;
    }

    const state = getState();
    const { selectedFolder, selectedProject, selectedStory } = state.projects;
    const folderParams = handleFolderId(selectedFolder);

    const projects = (await projectService.getUserProjects(folderParams)).data;
    const isProjectExist = projects.some((el) => el.projectId === selectedProject);
    if (!isProjectExist) {
      dispatch(setSelectedProject(getDefaultProject(projects)));
      return { projects };
    }
    dispatch(setFoldersAvailable(true));
    if (!selectedProject || !selectedStory) return {};
    const stories = (await storyService.getProjectStories(selectedProject, folderParams)).data;
    const isStoryExist = stories.some((el) => el.storyId === selectedStory);
    if (!isStoryExist) {
      dispatch(setSelectedStory(getDefaultStory(stories)));
      return { stories };
    }
    if (!selectedStory) return {};
    const finalCuts = await dispatch(fetchFinalcuts(selectedStory)).unwrap();
    dispatch(setSelectedFinalcut(getDefaultFc(finalCuts)));
    return {};
  }
);

export const removeFolder = createSafeAsyncThunk(SLICE_ID + '/removeFolder', async (folderId: FolderId) => {
  await foldersService.deleteFolder(folderId);
});

// projects

export const fetchProjects = createSafeAsyncThunk(SLICE_ID + '/fetchProjects', async (_, { getState }) => {
  const selectedFolder = getState().projects.selectedFolder;
  const response = await projectService.getUserProjects(handleFolderId(selectedFolder));
  return response.data;
});

export const createProject = createSafeAsyncThunk(
  SLICE_ID + '/createProject',
  async (projectName: string, { getState }) => {
    const folderId = getState().projects.selectedFolder;
    await projectService.createProject({ projectName, folderId: handleFolderId(folderId).folderId });
  }
);

export const removeProject = createSafeAsyncThunk(SLICE_ID + '/removeProject', async (projectId: ProjectId) => {
  await projectService.removeProject(projectId);
});

export const passProjectAccessedAt = createSafeAsyncThunk(
  SLICE_ID + '/passProjectAccessedAt',
  async (projectId: ProjectId) => {
    const response = await projectService.setProjectAccessedAt(projectId);
    return response.data;
  }
);

// stories

export const createStory = createSafeAsyncThunk(
  SLICE_ID + '/createStory',
  async (params: storyService.CreateStoryBody, { dispatch }) => {
    const response = await storyService.createStory(params);
    const story = response.data;
    // Fire-and-forget: TitleCreditsGenerator (mounted in App) uploads story-level webps.
    // Do not await — create/setup must not block on DOM render, and tests do not mount the generator.
    void dispatch(generateStoryTitleCreditsOverlays(story.storyId))
      .unwrap()
      .catch((error) => {
        console.error('[createStory] title/credits overlay generation failed', error);
      });
    return story;
  }
);

export const setStoryAudio = createSafeAsyncThunk(
  SLICE_ID + '/setStoryAudio',
  async (params: audioService.SetStoryAudio) => {
    await audioService.setStoryAudio(params);
  }
);

export const copyStory = createSafeAsyncThunk(SLICE_ID + '/copyStory', async (params: storyService.CopyStoryBody) => {
  const response = await storyService.copyStory(params);
  return response.data;
});

export const restoreFcStory = createSafeAsyncThunk(SLICE_ID + '/restoreFcStory', async (payload: RestoreFcStory) => {
  const { finalCutId, storyName } = payload;
  const response = await storyService.restoreFinalCutStory(finalCutId, storyName);
  return response.data;
});

export const removeStory = createSafeAsyncThunk(SLICE_ID + '/removeStory', async (storyId: StoryId) => {
  await storyService.removeStory(storyId);
});

export const fetchStories = createSafeAsyncThunk(
  SLICE_ID + '/fetchStories',
  async (projectId: ProjectId, { getState }) => {
    const selectedFolder = getState().projects.selectedFolder;
    const response = await storyService.getProjectStories(projectId, handleFolderId(selectedFolder));
    return response.data;
  }
);

export const passStoryAccessedAt = createSafeAsyncThunk(SLICE_ID + '/passStoryAccessedAt', async (storyId: StoryId) => {
  const response = await storyService.setStoryAccessedAt(storyId);
  return response.data;
});

// finalcuts

export const fetchFinalcuts = createSafeAsyncThunk(
  SLICE_ID + '/fetchStoryFinalCuts',
  async (storyId: StoryId, { dispatch, getState }) => {
    const state = getState();
    const fcProgress = state.fc__renderServer.fcProgress;
    const selectedFolder = state.projects.selectedFolder;
    const finalcuts = await fcService.getStoryFinalCuts(storyId, handleFolderId(selectedFolder));
    finalcuts.data.forEach((fc) => {
      if (fcProgress[fc.id] && isFcStatusCompleted(fc.status)) {
        dispatch(deleteProcessingFc(fc.id));
      }
    });
    return sortByDate(finalcuts.data, 'created');
  }
);

export const removeFinalcut = createSafeAsyncThunk(SLICE_ID + '/removeFinalCut', async (finalcutId: FinalcutId) => {
  await fcService.removeFinalCut(finalcutId);
});

export const reRenderFinalcut = createSafeAsyncThunk(SLICE_ID + '/reRenderFinalCut', async (finalcutId: FinalcutId) => {
  await fcService.reRenderFailedFinalCut(finalcutId);
});

// ***

export const updateEntity = createAsyncThunk(
  SLICE_ID + '/updateEntity',
  async (payload: UpdateEntity, { dispatch, getState, rejectWithValue }) => {
    const { projects, stories, finalcuts, folders } = (getState() as RootState).app__data;
    let previousName = '';
    try {
      dispatch(slice.actions.updateEntity(payload));
      switch (payload.entity) {
        case 'folders':
          previousName = folders?.find((el) => el.id === payload.id)?.name ?? '';
          return (await foldersService.updateFolderName(payload.id, payload.name)).data;
        case 'projects':
          previousName = projects?.find((project) => project.projectId === payload.id)?.name ?? '';
          return (await projectService.updateProject(payload)).data;
        case 'stories':
          previousName = stories?.find((story) => story.storyId === payload.id)?.name ?? '';
          return (await storyService.updateStory(payload)).data;
        case 'finalcuts':
          previousName = finalcuts?.find((finalcut) => finalcut.id === payload.id)?.name ?? '';
          return (await fcService.updateFinalcut(payload)).data;
      }
    } catch (error) {
      if (previousName) dispatch(slice.actions.updateEntity({ ...payload, name: previousName }));
      return rejectWithValue(Error.reduxErrorHandler(error));
    }
  }
);

// S E L E C T O R S

export const selectProject = createSelector(
  (state: RootState) => state.app__data.projects,
  (state: RootState) => state.projects.selectedProject,
  (projects, selectedProject) => {
    if (!projects || !selectedProject) return;
    return projects.find((project) => project.projectId === selectedProject);
  }
);

export const selectFinalCut = createSelector(
  (state: RootState) => state.app__data.finalcuts,
  (state: RootState) => state.projects.selectedFinalcut,
  (finalcuts, selectedFinalCut) => {
    if (!finalcuts.length || !selectedFinalCut) return;
    return finalcuts.find((finalcut) => finalcut.id === selectedFinalCut);
  }
);

export const selectStoryOptions = createSelector(
  (state: RootState) => state.app__data.stories,
  (stories) => stories?.map((el) => ({ value: el.storyId, label: el.name, disabled: !el.withFinalcuts }))
);

export const selectStory = createSelector(
  (state: RootState) => state.projects.selectedStory,
  (state: RootState) => state.app__data.stories,
  (selectedStory, stories) => stories?.find((el) => el.storyId === selectedStory) ?? null
);

export const selectFolder = createSelector(
  (state: RootState) => state.projects.selectedFolder,
  (state: RootState) => state.app__data.folders,
  (selectedFolder, folders) => folders?.find((el) => el.id === selectedFolder) ?? null
);

export const selectHelpMode = (state: RootState) => state.app__data.user?.isHelpMode;

// H E L P E R S

export const handleFolderId = (folderId?: FolderId | null) => {
  if (folderId === ALL_ACTIVE_FOLDER_ID) return { allActive: true };
  if (!folderId || folderId === UNFILED_FOLDER_ID) return {};
  return { folderId };
};

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    resetStories: (state) => {
      state.stories = null;
    },
    resetProjects: (state) => {
      state.projects = null;
    },
    resetFinalcuts: (state) => {
      state.finalcuts = [];
    },
    setHelpMode: (state, action: PayloadAction<boolean>) => {
      if (!state.user) return;
      state.user.isHelpMode = action.payload;
    },
    setStoryWithFinalcut: (state, action: PayloadAction<StoryWithFinalCut>) => {
      if (state.stories) {
        state.stories = state.stories.map((story) =>
          story.storyId !== action.payload.storyId ? story : { ...story, withFinalcuts: action.payload.withFinalcuts }
        );
      }
    },
    setRemovingInstance: (state, action: PayloadAction<RemovingRowInstance | null>) => {
      state.removingInstance = action.payload;
    },
    updateProjectStatusToUploading: (state, action: PayloadAction<ProjectId>) => {
      const project = state.projects?.find((project) => project.projectId === action.payload);
      if (project)
        if (project.status == ProjectStatus.Empty || project.status == ProjectStatus.Processed) {
          project.status = ProjectStatus.Uploading;
        }
    },
    updateProjectStatusToProcessing: (state, action: PayloadAction<ProjectId>) => {
      const project = state.projects?.find((project) => project.projectId === action.payload);
      if (project) project.status = ProjectStatus.Processing;
    },
    updateEntity: (state: State, action: PayloadAction<UpdateEntity>) => {
      const entity = action.payload.entity;
      if (entity === 'folders') {
        const folder = state.folders?.find((el) => el.id === action.payload.id);
        if (folder) folder.name = action.payload.name;
      }
      if (entity === 'projects') {
        const project = state.projects?.find((project) => project.projectId === action.payload.id);
        if (project) project.name = action.payload.name;
      }
      if (entity === 'stories') {
        const story = state.stories?.find((story) => story.storyId === action.payload.id);
        if (story) story.name = action.payload.name;
      }
      if (entity === 'finalcuts') {
        const storyFinalcut = state.finalcuts.find((finalcut) => finalcut.id === action.payload.id);
        if (storyFinalcut) storyFinalcut.name = action.payload.name;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(fetchFolders.fulfilled, (state, action) => {
      state.folders = action.payload;
    });
    builder.addCase(moveToFolder.fulfilled, (state, action) => {
      const { projects, stories } = action.payload;
      if (projects) state.projects = projects;
      if (stories) state.stories = stories;
    });
    builder.addCase(fetchStorageSize.fulfilled, (state, action) => {
      state.storage = action.payload;
    });
    builder.addCase(fetchPaymentPricing.fulfilled, (state, action) => {
      state.pricing = action.payload;
    });
    builder.addCase(fetchExtensions.fulfilled, (state, action) => {
      state.extensions = action.payload;
    });
    builder.addCase(fetchFonts.fulfilled, (state, action) => {
      state.fonts = action.payload;
    });
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      state.projects = action.payload;
    });
    builder.addCase(fetchStories.fulfilled, (state, action) => {
      state.stories = action.payload;
    });
    builder.addCase(fetchFinalcuts.fulfilled, (state, action) => {
      state.finalcuts = action.payload;
    });
    builder.addCase(reRenderFinalcut.fulfilled, (state, action) => {
      const finalcut = state.finalcuts?.find((finalcut) => finalcut.id === action.meta.arg);
      if (finalcut) finalcut.status = fcService.RenderStatus.pending;
    });
    builder.addCase(fetchFinalCut.fulfilled, (state, action) => {
      const finalcut = state.finalcuts?.find((finalcut) => finalcut.id === action.payload.id);
      if (finalcut) finalcut.status = action.payload.status;
    });
    builder.addCase(updateAccessType.fulfilled, (state, action) => {
      if (action.meta.arg.objectType === accessService.ObjectType.Project) {
        const project = state.projects?.find((project) => project.projectId === action.meta.arg.objectId);
        if (project) project.accessType = action.meta.arg.accessType;
      }
      if (action.meta.arg.objectType === accessService.ObjectType.Story) {
        const story = state.stories?.find((story) => story.storyId === action.meta.arg.objectId);
        if (story) story.accessType = action.meta.arg.accessType;
      }
      if (action.meta.arg.objectType === accessService.ObjectType.FinalCut) {
        const finalcut = state.finalcuts?.find((finalcut) => finalcut.id === action.meta.arg.objectId);
        if (finalcut) finalcut.accessType = action.meta.arg.accessType;
      }
    });
    builder.addCase(passStoryAccessedAt.fulfilled, (state, action) => {
      const story = state.stories?.find((el) => el.storyId === action.meta.arg);
      if (story) story.accessedAt = action.payload.data;
    });
    builder.addCase(passProjectAccessedAt.fulfilled, (state, action) => {
      const project = state.projects?.find((el) => el.projectId === action.meta.arg);
      if (project) project.accessedAt = action.payload.data;
    });
  },
});

export const {
  setHelpMode,
  resetStories,
  resetProjects,
  resetFinalcuts,
  setStoryWithFinalcut,
  setRemovingInstance,
  updateProjectStatusToUploading,
  updateProjectStatusToProcessing,
} = slice.actions;

export default slice.reducer;
